import { Button } from "../../../components/Button";
import { TextInput } from "../../../components/TextInput";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/x-close.svg";
import { ReactComponent as TrashIcon } from "../../../assets/icons/trash.svg";
import { ReactComponent as PaperClipIcon } from "../../../assets/icons/paper-clip.svg";
import { SelectInput } from "../../../components/SelectInput/SelectInput";
import { useApp } from "../../../services/app-context";
import { useLayoutEffect, useRef, useState, useEffect, useCallback } from "react";
import { getLabel } from "../utils/labels";
import { api } from "../../../services/api";
import { uploadFileToS3 } from "../../praxis-call/utils";
import { generateFileNameWithUUID } from "../../sales-aid/sales-aid-modal/SalesAidModal";
import { useUser } from "../../../services/user-context";
import { ShowToast } from "../../../services/toast";
import { Loading } from "../../../components/Loading/Loading";

type CallFlow = {
    id?: string;
    objection?: string;
    question?: string;
    category?: string;
    subtext?: string;
    attachment?: File | null;
    /** Server-side image path (e.g. app/assets/materials/uuid.png); display basename like create flow. */
    imageURL?: string;
    /** Original filename (or API-provided label) for display and save payload. */
    imageName?: string;
}

function attachmentDisplayName(callFlowItem: CallFlow, localFile: File | null): string {
    if (localFile) return localFile.name;
    if (callFlowItem.imageName) return callFlowItem.imageName;
    const url = callFlowItem.imageURL;
    if (url) {
        const seg = url.split("/").filter(Boolean);
        return seg.length ? seg[seg.length - 1] : url;
    }
    return "Attach Image";
}

type ObjectionItemProps = {
    callFlowItem: CallFlow;
    handleChange: any;
    patchCallFlowItem: (index: number, patch: Partial<CallFlow>) => void;
    index: number;
    handleDelete: any;
    callFlow: CallFlow[];
    tenantProfile: any;
    credentials: any;
    certificationId: string;
    certification_categories: any[];
    questionLabel: string;
    disabled?: boolean;
}

const filterOptions = (options: { label: string, value: string }[], callFlow: CallFlow[], category?: string) => {
    const selectedOptions = callFlow?.map((callFlowItem) => callFlowItem.category).filter((option) => option !== category);
    return options.filter((option) => !selectedOptions.includes(option.value));
}

const MIN_TEXTAREA_HEIGHT = 80;

function getTextareaContentHeight(el: HTMLTextAreaElement | null): number {
    if (!el) return MIN_TEXTAREA_HEIGHT;
    const prev = el.style.height;
    el.style.height = "0";
    const h = el.scrollHeight + 2;
    el.style.height = prev;
    return Math.max(MIN_TEXTAREA_HEIGHT, h);
}

function syncBothTextareaHeights(
    objectionEl: HTMLTextAreaElement | null,
    subtextEl: HTMLTextAreaElement | null
) {
    if (!objectionEl && !subtextEl) return;
    const h1 = getTextareaContentHeight(objectionEl);
    const h2 = getTextareaContentHeight(subtextEl);
    const height = Math.max(MIN_TEXTAREA_HEIGHT, h1, h2);
    if (objectionEl) {
        objectionEl.style.height = "0";
        objectionEl.style.height = `${height}px`;
    }
    if (subtextEl) {
        subtextEl.style.height = "0";
        subtextEl.style.height = `${height}px`;
    }
}

function ObjectionItem({ callFlowItem, handleChange, patchCallFlowItem, index, handleDelete, certification_categories, callFlow, tenantProfile, credentials, certificationId, questionLabel, disabled = false }: ObjectionItemProps) {
    const [attachment, setAttachment] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const objectionRef = useRef<HTMLTextAreaElement>(null);
    const subtextRef = useRef<HTMLTextAreaElement>(null);
    const [isUploading, setIsUploading] = useState(false);

    useLayoutEffect(() => {
        syncBothTextareaHeights(objectionRef.current, subtextRef.current);
    }, [callFlowItem.objection, callFlowItem.subtext]);

    /** Resolve current index by item id so we don't use a stale index after async work (e.g. if the list changed). */
    const getCurrentIndex = () => callFlow.findIndex((item) => item.id === callFlowItem.id);

    const handleUploadAttachment = async (file: File) => {
        if (disabled) return;
        try {
            setIsUploading(true);
            if (getCurrentIndex() === -1) {
                ShowToast({
                    message: `Could not attach image: this ${questionLabel} was removed.`,
                    type: "error",
                });
                return;
            }
            const fileName = generateFileNameWithUUID(file.name);
            const response = await uploadFileToS3(file, fileName, tenantProfile?.assets_bucket, credentials);
            if (response) {
                const key = response.split("/app")?.[1]?.split("?")?.[0];
                if (key) {
                    const imagePath = `app${key}`;
                    const indexAfterUpload = getCurrentIndex();
                    if (indexAfterUpload === -1) {
                        ShowToast({
                            message: "Could not attach image: this question was removed.",
                            type: "error",
                        });
                        return;
                    }
                    await api.attachImageToCertification(certificationId, {
                        index: indexAfterUpload,
                        imageURL: imagePath,
                        imageName: file.name,
                    });
                    const resolvedIndex = getCurrentIndex();
                    if (resolvedIndex !== -1) {
                        patchCallFlowItem(resolvedIndex, {
                            imageURL: imagePath,
                            imageName: file.name,
                            attachment: null,
                        });
                    }
                } else {
                    ShowToast({
                        message: "Error uploading an attachment",
                        type: "error",
                    });
                }
            } else {
                ShowToast({
                    message: "Failed to upload an attachment",
                    type: "error",
                });
            }
        } catch (error) {
            console.error(error);
            ShowToast({
                message: "Error uploading attachment",
                type: "error",
            });
        } finally {
            setIsUploading(false);
        }
    }

    const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) return;
        const file = e.target.files?.[0];
        if (file) {
            setAttachment(file);
            handleChange(index, "attachment", file);
            handleUploadAttachment(file);
        }
    }

    const handleDeleteAttachment = async (): Promise<boolean> => {
        if (disabled) return false;
        const currentIndex = getCurrentIndex();
        if (currentIndex === -1) return true; // item already removed from list
        try {
            setIsUploading(true);
            await api.deleteAttachment(certificationId, { index: currentIndex });
            const indexAfterDelete = getCurrentIndex();
            if (indexAfterDelete !== -1) {
                setAttachment(null);
                patchCallFlowItem(indexAfterDelete, {
                    attachment: null,
                    imageURL: undefined,
                    imageName: undefined,
                });
            }
            return true;
        } catch (error) {
            ShowToast({
                message: "Error deleting attachment",
                type: "error",
            });
            return false;
        } finally {
            setIsUploading(false);
        }
    }

    const onDelete = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (disabled) return;
        const hasAttachment = !!(attachment || callFlowItem?.attachment || callFlowItem?.imageURL || callFlowItem?.imageName);
        if (hasAttachment) {
            const deleted = await handleDeleteAttachment();
            if (!deleted) return;
        }
        const currentIndex = getCurrentIndex();
        if (currentIndex !== -1) handleDelete(e, currentIndex);
    }

    return (
        <div className="flex flex-col gap-1.5 relative">
            <div className="text-[13px] text-black font-medium">{questionLabel} {index + 1}:</div>
            <div className="flex items-stretch gap-2">
                <div className="flex-[3] flex flex-col">
                    <textarea
                        ref={objectionRef}
                        className="border min-h-[80px] overflow-hidden resize-none border-[#E9E9E9] w-full text-xs rounded px-2 py-1 !outline-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-80"
                        value={callFlowItem.objection || ""}
                        disabled={disabled}
                        onChange={(e) => {
                            handleChange(index, "objection", e.target.value);
                            requestAnimationFrame(() => requestAnimationFrame(() => syncBothTextareaHeights(objectionRef.current, subtextRef.current)));
                        }}
                    />
                </div>
                <div className="flex-[3] flex flex-col">
                    <textarea
                        ref={subtextRef}
                        className="border min-h-[80px] overflow-hidden resize-none border-[#E9E9E9] w-full text-xs rounded px-2 py-1 !outline-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-80"
                        value={callFlowItem.subtext || ""}
                        disabled={disabled}
                        onChange={(e) => {
                            handleChange(index, "subtext", e.target.value);
                            requestAnimationFrame(() => requestAnimationFrame(() => syncBothTextareaHeights(objectionRef.current, subtextRef.current)));
                        }}
                    />
                </div>
            </div>
            <div className="flex items-center gap-1 justify-between">
                <div className="flex-[2]">
                    <SelectInput
                        value={callFlowItem.category || ""}
                        onChange={(e) => handleChange(index, 'category', e.target.value)}
                        options={certification_categories}
                        placeholder="Select Messaging Flow"
                        className="w-full text-xs h-[33px]"
                        containerClassName="w-full"
                        disabled={disabled}
                    />
                </div>

                <div className="flex items-center gap-1 flex-[2] justify-between">
                    <button
                        type="button"
                        className="flex items-center gap-1 hover:opacity-80 mt-1.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50"
                        disabled={disabled}
                        onClick={() => !disabled && fileInputRef.current?.click()}
                    >
                        <PaperClipIcon className="w-4 h-4" />
                        <span className="text-xs text-[#4188C6] font-medium text-ellipsis overflow-hidden whitespace-nowrap max-w-[200px]">{attachmentDisplayName(callFlowItem, attachment)}</span>
                    </button>
                    {isUploading ?
                        <Loading />
                        : (attachment || callFlowItem.imageURL || callFlowItem.imageName) && (
                            <div className="flex items-center gap-1 mr-1">
                                <button type="button" disabled={disabled} onClick={handleDeleteAttachment}>
                                    <TrashIcon className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                </div>

            </div>

            <button type="button" className="absolute top-0 right-0 p-1 disabled:opacity-40" disabled={disabled} onClick={onDelete}>
                    <DeleteIcon className="w-4 h-4 [&_path]:stroke-[#898989]" />
                </button>
            <input type="file" ref={fileInputRef} onChange={handleAttachmentChange} className="hidden" accept="image/*" />
        </div>
    );
}


export const ObjectionList = ({
  onChange,
  callFlow = [],
  certificationId,
  categories,
  disabled = false,
}: {
  onChange: (callFlow: CallFlow[]) => void;
  callFlow: CallFlow[];
  certificationId: string;
  categories: any[];
  disabled?: boolean;
}) => {
    const { tenantProfile, certificationAssets } = useApp();
    const labels = certificationAssets?.labels ?? {};
    const questionLabel = getLabel("question.singular", labels);
    const { credentials } = useUser() as any;
    const hasNormalizedIds = useRef(false);

    const certification_categories = categories?.map((item: any) => ({
        label: item.label,
        value: item.key,
    }));

    /** Clear categories that are not in the current sales-aid list (e.g. after the document was changed). */
    useEffect(() => {
        if (!callFlow?.length || !categories?.length) return;
        const validKeys = new Set(
            categories.map((c: { key?: string }) => (c.key != null ? String(c.key) : "")).filter(Boolean)
        );
        let changed = false;
        const next = callFlow.map((item) => {
            const cat = item.category;
            if (cat == null || cat === "") return item;
            if (!validKeys.has(String(cat))) {
                changed = true;
                return { ...item, category: "" };
            }
            return item;
        });
        if (changed) onChange(next);
    }, [callFlow, categories, onChange]);

    // Ensure every item has a stable id so React keys stay correct when items are deleted (avoids state from one item appearing on another).
    useEffect(() => {
        if (!callFlow.length) return;
        if (callFlow.some((item) => !item.id)) hasNormalizedIds.current = false;
        if (hasNormalizedIds.current) return;
        if (callFlow.every((item) => item.id)) {
            hasNormalizedIds.current = true;
            return;
        }
        hasNormalizedIds.current = true;
        onChange(
            callFlow.map((item) => ({
                ...item,
                id: item.id ?? crypto.randomUUID(),
            }))
        );
    }, [callFlow]);

    const handleChange = (index: number, name: string, value: unknown) => {
        if (disabled) return;
        const newCallFlow = [...callFlow];
        newCallFlow[index] = {
            ...(newCallFlow[index] || {}),
            [name]: value,
        };
        onChange(newCallFlow);
    };

    const patchCallFlowItem = useCallback(
        (index: number, patch: Partial<CallFlow>) => {
            if (disabled) return;
            const newCallFlow = [...callFlow];
            newCallFlow[index] = {
                ...(newCallFlow[index] || {}),
                ...patch,
            };
            onChange(newCallFlow);
        },
        [callFlow, disabled, onChange]
    );
    const handleDelete = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        const newCallFlow = [...callFlow];
        newCallFlow.splice(index, 1);
        onChange(newCallFlow);
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-4 border border-lightGray rounded-[16px] p-2.5 bg-white">
                {callFlow?.map((callFlowItem, index) => (
                    <ObjectionItem
                        key={callFlowItem.id ?? index}
                        callFlowItem={callFlowItem}
                        handleChange={handleChange}
                        patchCallFlowItem={patchCallFlowItem}
                        index={index}
                        handleDelete={handleDelete}
                        certification_categories={filterOptions(certification_categories, callFlow, callFlowItem.category)}
                        callFlow={callFlow}
                        tenantProfile={tenantProfile}
                        credentials={credentials}
                        certificationId={certificationId}
                        questionLabel={questionLabel}
                        disabled={disabled}
                    />
                ))}
            </div>
            <Button
                variant="primary"
                className="text-[13px] !w-full !h-[46px] !rounded-[12px] !bg-gray-100 !px-2.5 border border-dashed border-text-gray-500 !text-gray-601"
                disabled={disabled}
                onClick={() => onChange([...callFlow, { id: crypto.randomUUID(), objection: "", category: "" }])}
            >
                <PlusIcon className="w-4 h-4 [&_path]:stroke-[#6C6C6C]" />
                {`Add ${questionLabel}`}
            </Button>
        </div>
    );
};
